import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createReagentAlt,
  deleteReagentAlt,
  readReagentAlt,
  updateReagentAlt,
} from "@/services/api/reagent-alt";
import {
  CreateReagentAltRequest,
  DeleteReagentAltRequest,
  ReadReagentAltResponse,
  UpdateReagentAltRequest,
} from "@/types/reagent-alt";
import { QUERY_KEYS } from "../queryKeys";

// 대체품 목록 조회 (전체 데이터 - 필터링/페이지네이션은 프론트에서 처리)
export const useReadReagentAlt = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: QUERY_KEYS.REAGENT_ALT.LIST(),
    queryFn: () => readReagentAlt(),
    enabled: options?.enabled ?? true,
  });
};

// 대체품 자사품 추가 (낙관적 업데이트)
export const useCreateReagentAlt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReagentAltRequest) => createReagentAlt(data),

    onMutate: async (newData) => {
      const queryKey = QUERY_KEYS.REAGENT_ALT.LIST();

      // 진행 중인 refetch 취소
      await queryClient.cancelQueries({ queryKey });

      // 이전 데이터 저장 (롤백용)
      const previousData =
        queryClient.getQueryData<ReadReagentAltResponse>(queryKey);

      // 낙관적 업데이트
      if (previousData) {
        const existingItemIndex = previousData.items.findIndex(
          (item) => item.no === newData.alt_item_no
        );

        if (existingItemIndex >= 0) {
          // 기존 대체품에 자사품 추가
          const updatedItems = [...previousData.items];
          const existingItem = updatedItems[existingItemIndex];
          const newOwnItems = newData.own_item_nos.map((no) => ({
            no,
            name: "",
            spec: "",
            list_price: "",
            division: "",
          }));
          updatedItems[existingItemIndex] = {
            ...existingItem,
            own_items: [...(existingItem.own_items || []), ...newOwnItems],
          };

          queryClient.setQueryData<ReadReagentAltResponse>(queryKey, {
            ...previousData,
            items: updatedItems,
          });
        } else {
          // 새 대체품 추가
          const newItem = {
            no: newData.alt_item_no,
            name: newData.alt_item_name,
            spec: newData.alt_item_spec,
            manufacturer: newData.alt_name,
            own_items: newData.own_item_nos.map((no) => ({
              no,
              name: "",
              spec: "",
              list_price: "",
              division: "",
            })),
          };

          queryClient.setQueryData<ReadReagentAltResponse>(queryKey, {
            ...previousData,
            total_count: previousData.total_count + 1,
            items: [newItem, ...previousData.items],
          });
        }
      }

      return { previousData, queryKey };
    },

    onError: (_error, _newData, context) => {
      // 에러 시 롤백
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },

    onSettled: () => {
      // 완료 후 서버 데이터와 동기화
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.REAGENT_ALT.LIST(),
      });
    },
  });
};

// 대체품 수정 (낙관적 업데이트)
export const useUpdateReagentAlt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateReagentAltRequest) => updateReagentAlt(data),

    onMutate: async (updateData) => {
      const queryKey = QUERY_KEYS.REAGENT_ALT.LIST();

      // 진행 중인 refetch 취소
      await queryClient.cancelQueries({ queryKey });

      // 이전 데이터 저장 (롤백용)
      const previousData =
        queryClient.getQueryData<ReadReagentAltResponse>(queryKey);

      // 낙관적 업데이트
      if (previousData) {
        const updatedItems = previousData.items.map((item) =>
          item.no === updateData.alt_item_no
            ? {
                ...item,
                name: updateData.alt_item_name,
                spec: updateData.alt_item_spec,
                manufacturer: updateData.alt_name,
              }
            : item
        );

        queryClient.setQueryData<ReadReagentAltResponse>(queryKey, {
          ...previousData,
          items: updatedItems,
        });
      }

      return { previousData, queryKey };
    },

    onError: (_error, _updateData, context) => {
      // 에러 시 롤백
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },

    onSettled: () => {
      // 완료 후 서버 데이터와 동기화
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.REAGENT_ALT.LIST(),
      });
    },
  });
};

// 대체품 삭제 (낙관적 업데이트)
export const useDeleteReagentAlt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeleteReagentAltRequest) => deleteReagentAlt(data),

    onMutate: async (deleteData) => {
      const queryKey = QUERY_KEYS.REAGENT_ALT.LIST();

      // 진행 중인 refetch 취소
      await queryClient.cancelQueries({ queryKey });

      // 이전 데이터 저장 (롤백용)
      const previousData =
        queryClient.getQueryData<ReadReagentAltResponse>(queryKey);

      // 낙관적 업데이트
      if (previousData) {
        if (deleteData.own_item_nos && deleteData.own_item_nos.length > 0) {
          // 자사품만 삭제
          const updatedItems = previousData.items.map((item) => {
            if (item.no === deleteData.alt_item_no) {
              return {
                ...item,
                own_items: (item.own_items || []).filter(
                  (ownItem) => !deleteData.own_item_nos?.includes(ownItem.no)
                ),
              };
            }
            return item;
          });

          queryClient.setQueryData<ReadReagentAltResponse>(queryKey, {
            ...previousData,
            items: updatedItems,
          });
        } else {
          // 대체품 전체 삭제
          const filteredItems = previousData.items.filter(
            (item) => item.no !== deleteData.alt_item_no
          );

          queryClient.setQueryData<ReadReagentAltResponse>(queryKey, {
            ...previousData,
            total_count: previousData.total_count - 1,
            items: filteredItems,
          });
        }
      }

      return { previousData, queryKey };
    },

    onError: (_error, _deleteData, context) => {
      // 에러 시 롤백
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },

    onSettled: () => {
      // 완료 후 서버 데이터와 동기화
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.REAGENT_ALT.LIST(),
      });
    },
  });
};
